import { useMemo } from "react"
import {
  Avatar,
  Box,
  CardContent,
  CardMedia,
  Typography,
  useTheme,
  Button,
} from "@mui/material"
import size from "lodash/size"
import { deepmerge } from "@mui/utils"
import { getIcon } from "utils/icon"

import { useAppUI } from "providers/ui"
import { useTranslation } from "providers/i18n"
import { useMobileUrl } from "hooks/useMobileUrl"

import { MotionDiv } from "components/motions"
import {
  StoreIcon,
  CloseIcon,
  CalendarIcon,
  BulletedListIcon,
  ClockIcon,
} from "components/icons"
import { FeatureLabel } from "components/domains/features/FeatureLabel"
import { GetFeatureDetailResult } from "utils/feature"
import { QRCode } from "components/QRCode"
import { CloseIconButton } from "components/Button/CloseIconButton"
import { Carousel, CarouselItem } from "components/Carousel"

import { LocationIcon } from "components/icons"
import { useImageModal } from "hooks/imageModal/useImageModal"
import { ImageCarouselModal } from "components/Image"
import { ComingSoonChip, NewShopChip } from "components/chips"
import { useGeoLocation } from "providers/geolocation"

export type PlaceDetailData = GetFeatureDetailResult & { [k: string]: any }
export interface IPlaceDetailSideBarProps {
  data: PlaceDetailData
  showAdditionalButtons?: boolean
  onNavigate?: () => void
  onClickPromotionButton?: () => void
}

export const PlaceDetailSideBar: React.FC<IPlaceDetailSideBarProps> = ({
  data = {},
  onNavigate = () => {},
  onClickPromotionButton = () => {},
}) => {
  const { goBack } = useAppUI()
  const { t } = useTranslation()
  const theme = useTheme()
  const { mobileUrl } = useMobileUrl()
  const { deviceLocation } = useGeoLocation()

  const {
    logo,
    name,
    description,
    categoryIconProp = {},
    hours,
    venueLogo,
    venueName,
    category,
    gallery,
    promotions,
    isNavigatable = true,
    isNew,
    isComingSoon,
    startDate,
    allLevels,
  } = data as PlaceDetailData

  const isReadyToNavigate = !!deviceLocation && isNavigatable
  const hasPromotions = size(promotions) > 0
  const hasGallery = size(gallery)
  const galleryUrls = useMemo(
    () => (size(gallery) > 0 ? gallery?.map((v) => v?.url) : []) as string[],
    [gallery]
  )
  const { handleImage, openModal, activeImage, handleClose } = useImageModal()

  const opening = startDate?.format("DD/MM/YYYY") || "-"

  const {
    root,
    headerContainer,
    contentContainer,
    descriptionContainer,
    descriptionText,
    titleText,
    venueNameText,
    avatarContainer,
    avatarRoot,
    qrCodeContainer,
    qrCodeCaptionContainer,
    qrCode,
    sidebarContent,
    infoListContainer,
    infoListItem,
    infoLabel,
    openingLabel,
    qrCodePrimaryText,
    qrCodeSecondaryText,
    closeButton,
    infoContainer,
    actionContainer,
    actionButton,
    galleryCarouselWrapper,
    galleryCarousel,
    galleryCarouselItem,
    galleryImage,
    galleryCarouselModal,
    newShopChip,
    comingSoonChip,
    venueLogoStyle,
    headerContentWrapper,
    promotionButton,
  } = theme.components[`PlaceDetailSideBar`]?.styleOverrides || {}
  const { galleryCarouselOption, operationTimeOption } =
    theme.components[`PlaceDetailSideBar`]?.defaultProps || {}

  const sideBarStyleOverrides =
    theme.components[`AppSidebar`]?.styleOverrides || {}
  const { wrapper, body } = sideBarStyleOverrides

  const closeIcon = useMemo(
    () => getIcon(theme?.assets?.closeIcon) || <CloseIcon />,
    [theme]
  )

  const calendarIcon = useMemo(
    () => getIcon(theme?.assets?.calendarIcon) || <CalendarIcon />,
    [theme]
  )

  const locationIcon = useMemo(
    () => getIcon(theme?.assets?.floorIcon) || <LocationIcon />,
    [theme]
  )

  const listIcon = useMemo(
    () => getIcon(theme?.assets?.listIcon) || <BulletedListIcon />,
    [theme]
  )

  const clockIcon = useMemo(
    () => getIcon(theme?.assets?.clockIcon) || <ClockIcon />,
    [theme]
  )

  const allLevelDisplay = useMemo(
    () => allLevels?.join(", ") || "-",
    [allLevels]
  )

  return (
    <>
      <MotionDiv
        sx={deepmerge(wrapper, sidebarContent)}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.25 }}
      >
        <Box sx={body}>
          <Box sx={root}>
            <CardContent sx={contentContainer}>
              <CloseIconButton
                sx={closeButton}
                icon={closeIcon}
                onClick={goBack}
                size="large"
              />

              <Box sx={headerContainer}>
                <Box sx={avatarContainer}>
                  <Avatar sx={avatarRoot} src={logo}>
                    {getIcon(categoryIconProp, null, {
                      imageIconProps: {
                        solidColorFilterId: "placeDetail_avatar_filter",
                      },
                    }) || <StoreIcon />}
                  </Avatar>

                  <CardMedia
                    component="img"
                    src={venueLogo}
                    sx={venueLogoStyle}
                  />
                </Box>
                <Box sx={headerContentWrapper}>
                  {isNew && <NewShopChip sx={newShopChip} />}
                  {isComingSoon && (
                    <ComingSoonChip
                      sx={comingSoonChip}
                      label={t("Coming soon")}
                    />
                  )}
                  <Typography sx={titleText}>{t(name)}</Typography>
                  <Typography sx={venueNameText}>
                    {t(venueName ?? "")}
                  </Typography>
                </Box>
              </Box>

              <Box sx={infoContainer}>
                <Box sx={infoListContainer}>
                  <Box sx={infoListItem}>
                    {locationIcon}
                    <FeatureLabel
                      label={t("Floor")}
                      value={t(allLevelDisplay) || "-"}
                      sx={infoLabel}
                    />
                  </Box>
                  <Box sx={infoListItem}>
                    {listIcon}
                    <FeatureLabel
                      label={t("Category")}
                      value={t(category) || "-"}
                      sx={infoLabel}
                    />
                  </Box>
                  {isComingSoon ? (
                    <Box sx={infoListItem} className="is-coming-soon">
                      {calendarIcon}
                      <FeatureLabel
                        label={t("Opening")}
                        value={opening || "-"}
                        sx={deepmerge(infoLabel, openingLabel)}
                      />
                    </Box>
                  ) : (
                    <Box sx={infoListItem}>
                      {clockIcon}
                      <FeatureLabel
                        label={t("Operation Time")}
                        value={hours || "-"}
                        sx={infoLabel}
                        multiLine={operationTimeOption.multiline}
                      />
                    </Box>
                  )}
                </Box>
              </Box>

              <Box sx={descriptionContainer}>
                {description && (
                  <Typography sx={descriptionText} variant="body1">
                    {t(description)}
                  </Typography>
                )}
              </Box>

              {!!hasGallery && (
                <Box sx={galleryCarouselWrapper}>
                  <Carousel
                    options={{
                      padding: 0,
                      gap: 24,
                      pagination: false,
                      autoplay: true,
                      arrows: true,
                      rewind: false,
                      ...galleryCarouselOption,
                    }}
                    sx={galleryCarousel}
                  >
                    {gallery?.map((image, idx) => {
                      return (
                        <CarouselItem
                          key={`${image.name}-${idx}`}
                          sx={galleryCarouselItem}
                        >
                          <CardMedia
                            component={"img"}
                            onClick={() => handleImage(image.url)}
                            src={`${image.url}`}
                            alt={`${image.name}-${idx}`}
                            sx={galleryImage}
                          />
                        </CarouselItem>
                      )
                    })}
                  </Carousel>
                </Box>
              )}

              <Box
                sx={qrCodeContainer}
                className={hasGallery ? "has-gallery" : ""}
              >
                <QRCode sx={qrCode} url={mobileUrl} />
                <Box sx={qrCodeCaptionContainer}>
                  <Typography
                    variant="subtitle2"
                    component="p"
                    sx={qrCodePrimaryText}
                  >
                    {t("Scan Here")}
                  </Typography>
                  <Typography
                    variant="caption"
                    component="p"
                    sx={qrCodeSecondaryText}
                  >
                    {t("For Mobile")}
                  </Typography>
                </Box>
              </Box>

              <Box sx={actionContainer}>
                {hasPromotions && !isComingSoon && (
                  <Button
                    variant="outlined"
                    onClick={onClickPromotionButton}
                    sx={deepmerge(actionButton, promotionButton)}
                  >
                    {t("Promotion")}
                  </Button>
                )}
                {isReadyToNavigate && (
                  <Button
                    id="get-direction-button"
                    data-testid="get-direction-button"
                    variant="contained"
                    onClick={onNavigate}
                    className="highlight"
                    sx={{
                      ...actionButton,
                      visibility: isNavigatable ? "visible" : "hidden",
                    }}
                    disabled={isComingSoon}
                  >
                    {isComingSoon ? t("Coming soon") : t("Get direction")}
                  </Button>
                )}
              </Box>
            </CardContent>
          </Box>
        </Box>
      </MotionDiv>
      <ImageCarouselModal
        className="PlaceDetailSideBar-galleryCarouselModal"
        sx={galleryCarouselModal}
        activeSrc={activeImage}
        open={openModal}
        imageSrcs={galleryUrls}
        onClose={handleClose}
        carouselOptions={{
          rewind: false,
        }}
      />
    </>
  )
}
